import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { FileUploadResult } from 'components/upload/types';
import { UploadDialog } from 'components/upload/uploadFileDialog';
import { LayoutSelectors } from 'layout';
import { MessageLibraryItem } from 'library/duck/types';
import { NewAdDialog } from 'library/edit/mediaTypes/ad/NewAdDialog';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { DataGridContextProvider } from 'react-tools';

import Paper from '@material-ui/core/Paper';
import { MediaDto, MediaUpdateDto } from '@models';
import { MediaType } from '@models/media/MediaType';

import { LibraryActions, LibrarySelectors, LibraryThunks } from '../duck';
import { NavigatorList } from './list/NavigatorList';
import { useStyles } from './Navigator.jss';
import { NavigatorToolbar } from './NavigatorToolbar';
import { NavigatorTable } from './table/NavigatorTable';

export interface NavigatorProps {
  variant: 'table' | 'list';
  secondaryAction?: React.FunctionComponent<{
    item: MessageLibraryItem;
    [key: string]: any;
  }>;
  secondaryActionProps?: {
    [key: string]: any;
  };
  secondaryActionLabel?: string;
  hideControls?: boolean;
  disableSelection?: boolean;
  onSelection?: (id: number) => void;
  selected?: number;
}

export const Navigator: React.FunctionComponent<NavigatorProps> = (props) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const history = useHistory();

  const { selected, variant, onSelection, hideControls, disableSelection } = props;

  const dispatch = useDispatch();
  const [sortKey, setSortKey] = React.useState<'creationDate' | 'name'>('creationDate');
  const [adProviderState, setAdProviderState] = React.useState<boolean>(false);
  const selectedMessageType = useSelector(LayoutSelectors.selectLayoutState);
  const libraryItems = useSelector((state) => LibrarySelectors.selectMessages(state, sortKey));
  const currentFolder = useSelector(LibrarySelectors.selectCurrentFolder);
  const fetchingMessages = useSelector(LibrarySelectors.selectFetchingLibrary);

  const [items, setItems] = React.useState<MessageLibraryItem[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
  const [newAdDialogOpen, setNewAdDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);

  const [newlyAdded, setNewlyAdded] = React.useState<number[]>([]);
  const tableContainer = React.useRef<HTMLDivElement>(null);

  const handleNewMedia = useCallback((mediaType: MediaType) => {
    switch (mediaType) {
      case MediaType.Audio:
        setUploadDialogOpen(true);
        break;
      case MediaType.Ad:
        setNewAdDialogOpen(true);
        break;
    }
  }, []);

  const handleUploadCancel = useCallback(() => setUploadDialogOpen(false), [setUploadDialogOpen]);
  const handleNewAdCancel = useCallback(() => setNewAdDialogOpen(false), [setNewAdDialogOpen]);

  const handleDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true);
  }, [selected, props.selected, dispatch]);

  const onDelete = useCallback(() => {
    if (selected) {
      dispatch(LibraryThunks.deleteMessages([selected]));
      setDeleteDialogOpen(false);
    }
  }, [deleteDialogOpen, setDeleteDialogOpen, selected]);

  const onCancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
  }, [deleteDialogOpen, setDeleteDialogOpen]);

  const handleSortClick = useCallback(
    (sortKey: 'name' | 'creationDate') => {
      setSortKey(sortKey);
    },
    [setSortKey]
  );

  const onMessageUpload = useCallback(
    (files: FileUploadResult[]) => {
      const medias = files.reduce<MediaDto[]>((a, i) => {
        if (i.media) {
          a.push(i.media);
        }
        return a;
      }, []);
      setNewlyAdded(medias.map((f) => f.id));
      setUploadDialogOpen(false);
      dispatch(LibraryActions.addMessages(selectedMessageType.selectedItem, medias));
    },
    [dispatch, setNewlyAdded, selectedMessageType.selectedItem]
  );

  const onMediaCreate = useCallback(
    (mediaUpdateDto: MediaUpdateDto) => {
      const newMedia = { ...mediaUpdateDto, mediaFolderId: currentFolder.id };
      dispatch(LibraryThunks.createMedia(selectedMessageType.selectedItem, newMedia));
      setNewAdDialogOpen(false);
    },
    [currentFolder, dispatch, selectedMessageType]
  );

  useEffect(() => {
    if (tableContainer.current) {
      tableContainer.current.scrollTop = 0;
    }
  }, [newlyAdded, tableContainer.current]);

  useEffect(() => {
    setItems(libraryItems);
    setNewlyAdded([]);
  }, [libraryItems]);

  useEffect(() => {
    if (selected === undefined || selected === 0) {
      return;
    }

    const selectedDeleted = libraryItems.filter((i) => i.mediaInfo.id === selected).length === 0;
    if (selectedDeleted && onSelection) {
      onSelection(0);
    }
  }, [libraryItems, selected, onSelection]);

  const handleBackFolderClick = useCallback(() => {
    dispatch(LibraryActions.popFolder());
  }, [currentFolder, dispatch]);

  const handleItemSelected = useCallback(
    (item: MessageLibraryItem) => {
      if (item.mediaInfo.isFolder) {
        dispatch(LibraryActions.pushFolder(item));
      } else if (!disableSelection) {
        if (variant === 'list') {
          history.push(`/library/message/${item.mediaInfo.id}`);
        } else if (onSelection) {
          onSelection(item.mediaInfo.id);
        }
      }
    },
    [history, onSelection, disableSelection]
  );

  // test if adProvider is enabled on the server
  useEffect(() => {
    LibraryThunks.getAdProviderState().then((response: boolean) => setAdProviderState(response));
  }, []);

  const onSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const searchTerm = event.target.value;
      setItems(libraryItems.filter((i) => i.mediaInfo.name._contains(searchTerm)));
    },
    [libraryItems, setItems]
  );

  return (
    <DataGridContextProvider>
      <div className={classes.container}>
        <NavigatorToolbar
          currentFolder={currentFolder}
          hideControls={hideControls}
          onSearchChange={variant === 'list' ? onSearchChange : undefined}
          onNewMedia={handleNewMedia}
          onDelete={handleDeleteClick}
          onFolderBack={handleBackFolderClick}
          onSort={handleSortClick}
          deleteDisabled={!selected}
          adProviderEnabled={adProviderState}
        />
        {props.variant === 'table' && (
          <Paper className={classes.tablePaper}>
            <NavigatorTable
              libraryItems={items}
              fetchingMessages={fetchingMessages}
              newlyAdded={newlyAdded}
              disableSelection={props.disableSelection}
              secondaryAction={props.secondaryAction}
              secondaryActionProps={props.secondaryActionProps}
              secondaryActionLabel={props.secondaryActionLabel}
              onItemSelected={handleItemSelected}
              currentFolder={currentFolder}
              controlledSelection={props.selected}
              controlledSelectionEnabled={props.selected !== undefined}
            />
          </Paper>
        )}
        {props.variant === 'list' && (
          <NavigatorList
            libraryItems={items}
            onItemSelected={handleItemSelected}
            fetchingMessages={fetchingMessages}
            newlyAdded={newlyAdded}
            secondaryAction={props.secondaryAction}
            secondaryActionProps={props.secondaryActionProps}
          />
        )}
      </div>

      <UploadDialog
        accept=".mp3"
        open={uploadDialogOpen}
        multiple
        filesAreContent
        folderId={currentFolder?.id}
        onFinish={onMessageUpload}
        onCancel={handleUploadCancel}
        title="Upload messages"
      />
      <NewAdDialog open={newAdDialogOpen} onCancel={handleNewAdCancel} onSave={onMediaCreate} />
      <ConfirmationDialog
        title={t('deleteMessageConfirmTitle')}
        open={deleteDialogOpen}
        content={t('deleteMessageContent')}
        onConfirm={onDelete}
        onCancel={onCancelDelete}
      />
    </DataGridContextProvider>
  );
};
